import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Building2,
  Tag,
  NotepadText,
  MessagesSquare,
  Mails,
  Clock4,
  Clock,
  CalendarDays,
  AtSign,
  DollarSign,
  Percent,
  UserCircle,
  Signature,
  CheckCircle2,
  User,
} from "lucide-react";
import {
  FIELD_ICON_BY_ID,
  FIELD_ICON_BY_TYPE,
  DEFAULT_FIELD_ICON,
} from "../../constants/fieldIconMap";
import { format, isValid } from "date-fns";
import { fromJS } from "immutable";
import { getJirItemUrl } from "../../api/api";
import { navigateTo } from "../../Navigator";
import LabelsSelect from "../../components/LabelsSelect";
import TaskAction from "../../actions/TaskAction";
import { refreshInteractionStatistics } from "../../api/ProjectApi";
import { DEFAULT_FIELDS_BY_TYPE } from "../../main/component/view/CardRowSettings";
import LinkItemPickerV2 from "../../main/component/LinkItemPickerV2";
import AmountField from "../../components/AmountField";
import LabelPicker from "./TaskDetail/LabelPicker";
import HoverManager from "../../components/HoverManager";
import ActivityIndicator from "../../activities/ActivityIndicator";
import NameHoverCard from "./NameHoverCard";

/** Attribute row: optional label above, icon + value below. Hidden if children is empty. */
const AttrRow: React.FC<{
  icon: React.ReactNode;
  label?: string;
  showLabel?: boolean;
  children: React.ReactNode;
}> = ({ icon, label, showLabel, children }) => {
  if (!children) return null;
  return (
    <div className="py-[2px]">
      {showLabel && label && (
        <div className="text-[12px] font-medium text-[#242529] leading-[16px] mb-px">{label}</div>
      )}
      <div className="flex items-center gap-1.5 min-h-[20px]">
        {!showLabel && (
          <span className="shrink-0 w-4 h-4 flex items-center justify-center text-gray-400">
            {icon}
          </span>
        )}
        <span className="truncate text-[13px] text-gray-600 leading-5">{children}</span>
      </div>
    </div>
  );
};

/** Footer stat badge with custom tooltip. Always shows icon, hides count value when 0/empty. */
const StatItem: React.FC<{
  icon: React.ReactNode;
  count: number | string;
  onClick?: (e: React.MouseEvent) => void;
  tooltip?: string;
}> = ({ icon, count, onClick, tooltip }) => {
  const hasValue = count && count !== 0 && count !== "0";
  return (
    <div
      onClick={onClick}
      className={`relative group/stat flex items-center gap-1 text-xs text-gray-400 ${onClick ? "cursor-pointer hover:text-gray-600 transition-colors" : ""}`}
    >
      <span className="shrink-0">{icon}</span>
      {hasValue && <span>{count}</span>}
      {tooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[11px] text-white bg-gray-800 rounded whitespace-nowrap opacity-0 invisible group-hover/stat:opacity-100 group-hover/stat:visible transition-all duration-150 pointer-events-none z-50">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
};

/** Footer badge with icon, text, and hover tooltip */
const TooltipBadge: React.FC<{
  icon: React.ReactNode;
  text: string;
  tooltip: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}> = ({ icon, text, tooltip, className = "", onClick }) => (
  <div
    className={`relative group/badge flex items-center gap-1 cursor-pointer transition-colors ${className}`}
    onClick={onClick}
  >
    {icon}
    <span className="text-xs">{text}</span>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[11px] text-white bg-gray-800 rounded whitespace-nowrap opacity-0 invisible group-hover/badge:opacity-100 group-hover/badge:visible transition-all duration-150 pointer-events-none z-50">
      {tooltip}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
    </div>
  </div>
);

/** Format date value to dd-MM-yyyy */
const formatDateValue = (value: string): string => {
  if (!value) return "";
  const date = new Date(value);
  if (!isValid(date)) return value;
  return format(date, "dd-MM-yyyy");
};

/** Safely extract an integer field from Immutable task data */
const getIntField = (task: any, field: string): number =>
  parseInt(task.getIn(["data", field, "value"]) || "0", 10) || 0;

/** Convert a date string to relative days (e.g. "today", "3d") */
const formatRelativeDays = (dateStr: string): { label: string; days: number } => {
  if (!dateStr) return { label: "", days: 0 };
  const d = new Date(dateStr);
  if (!isValid(d)) return { label: "", days: 0 };
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return { label: "today", days: 0 };
  if (days === 1) return { label: "1d", days: 1 };
  return { label: `${days}d`, days };
};

/** Manages hover card open/close with delay timers */
const useHoverCard = (openDelay = 400, closeDelay = 150) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const cancel = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const open = useCallback(() => {
    cancel();
    openTimer.current = setTimeout(() => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({ top: rect.top - 4, left: rect.left });
      }
      setVisible(true);
    }, openDelay);
  }, [cancel, openDelay]);

  const scheduleClose = useCallback(() => {
    cancel();
    closeTimer.current = setTimeout(() => setVisible(false), closeDelay);
  }, [cancel, closeDelay]);

  const keepOpen = useCallback(() => {
    cancel();
  }, [cancel]);

  return { visible, position, anchorRef, open, scheduleClose, keepOpen };
};

/** Resolve icon from centralized fieldIconMap for card-size rendering */
const cardIcon = (fieldId: string): React.ReactNode => {
  const cls = "w-3.5 h-3.5";
  const entry = FIELD_ICON_BY_ID[fieldId];
  if (entry) {
    const Icon = entry.icon;
    return <Icon className={cls} />;
  }
  const byType = FIELD_ICON_BY_TYPE[fieldId];
  if (byType) {
    const Icon = byType;
    return <Icon className={cls} />;
  }
  const Fallback = DEFAULT_FIELD_ICON;
  return <Fallback className={cls} />;
};

/** Map of field IDs to their icons and data extractors */
const FIELD_CONFIG: Record<
  string,
  {
    icon: React.ReactNode;
    label: string;
    extract: (task: any, taskType: string) => any;
    render?: (value: any, taskType: string) => React.ReactNode;
  }
> = {
  name: {
    icon: null,
    label: "Name",
    extract: (task) => task.getIn(["data", "name", "value"]) || task.get("title") || "",
  },
  industry: {
    icon: cardIcon("industry"),
    label: "Industry",
    extract: (task) => task.getIn(["data", "industry", "value"]) || "",
  },
  contact: {
    icon: cardIcon("contact"),
    label: "Contact",
    extract: (task) => task.getIn(["data", "contact", "value", "label"]) || "",
  },
  account: {
    icon: cardIcon("account"),
    label: "Account",
    extract: (task) => task.getIn(["data", "account", "value", "label"]) || "",
  },
  description: {
    icon: cardIcon("description"),
    label: "Desc",
    extract: (task) => task.getIn(["data", "description", "value"]) || "",
  },
  country: {
    icon: cardIcon("country"),
    label: "Country",
    extract: (task) => {
      const label = task.getIn(["data", "country", "value", "label"]);
      if (label) return label;
      const val = task.getIn(["data", "country", "value"]);
      if (val && typeof val === "string") return val;
      return "";
    },
  },
  labels: {
    icon: cardIcon("labels"),
    label: "Labels",
    extract: (task) => task.getIn(["data", "labels", "value"]),
    render: null,
  },
  openedAt: {
    icon: <CalendarDays className="w-3.5 h-3.5" />,
    label: "Created",
    extract: (task) =>
      task.getIn(["data", "openedAt", "value"]) || task.getIn(["data", "start", "value"]) || "",
    render: (value) => <>{formatDateValue(value)}</>,
  },
  birthDate: {
    icon: <CalendarDays className="w-3.5 h-3.5" />,
    label: "Birthday",
    extract: (task) => task.getIn(["data", "birthDate", "value"]) || "",
    render: (value) => <>{formatDateValue(value)}</>,
  },
  email: {
    icon: <AtSign className="w-3.5 h-3.5" />,
    label: "Email",
    extract: (task) => task.getIn(["data", "email", "value"]) || "",
  },
  phone: {
    icon: cardIcon("phone"),
    label: "Phone",
    extract: (task) => task.getIn(["data", "phone", "value"]) || "",
  },
  website: {
    icon: cardIcon("website"),
    label: "Website",
    extract: (task) => task.getIn(["data", "website", "value"]) || "",
  },
  salutation: {
    icon: <UserCircle className="w-3.5 h-3.5" />,
    label: "Title",
    extract: (task) => task.getIn(["data", "salutation", "value"]) || "",
  },
  jobTitle: {
    icon: <Signature className="w-3.5 h-3.5" />,
    label: "Job",
    extract: (task) => task.getIn(["data", "jobTitle", "value"]) || "",
  },
  estimatedRevenue: {
    icon: <DollarSign className="w-3.5 h-3.5" />,
    label: "Value",
    extract: (task) => task.getIn(["data", "estimatedRevenue", "value"]) || "",
    render: (value) => {
      const num = parseFloat(value);
      if (isNaN(num)) return <>{value}</>;
      return <span className="font-medium text-green-600">${num.toLocaleString()}</span>;
    },
  },
  estimatedProbability: {
    icon: <Percent className="w-3.5 h-3.5" />,
    label: "Prob.",
    extract: (task) => task.getIn(["data", "estimatedProbability", "value"]) || "",
    render: (value) => <>{value}%</>,
  },
  issue: {
    icon: cardIcon("issue"),
    label: "Issue",
    extract: (task) => task.getIn(["data", "issue", "value"]) || "",
  },
  stage: {
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    label: "Stage",
    extract: (task) => task.getIn(["data", "stage", "value"]) || "",
  },
};

/** Skeleton loading placeholder matching the BoardCard layout */
export const BoardCardSkeleton: React.FC<{ rows?: number }> = ({ rows = 2 }) => (
  <div className="w-full animate-pulse">
    {/* Name row skeleton */}
    <div className="flex items-center gap-1.5 mb-1.5 pr-5">
      <div className="shrink-0 w-4 h-4 bg-gray-200 rounded-full" />
      <div className="h-4 bg-gray-200 rounded w-3/4" />
    </div>

    {/* Attribute rows skeleton */}
    {rows > 0 && (
      <div className="space-y-1.5">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-1.5 min-h-[20px]">
            <div className="shrink-0 w-4 h-4 bg-gray-100 rounded" />
            <div
              className="h-3.5 bg-gray-100 rounded"
              style={{ width: `${55 + (i % 3) * 15}%`, animationDelay: `${i * 100}ms` }}
            />
          </div>
        ))}
      </div>
    )}

    {/* Footer skeleton */}
    <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-gray-100">
      <div className="flex items-center gap-3">
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className="flex items-center gap-1">
            <div className="w-4 h-4 bg-gray-100 rounded" />
            <div className="w-3 h-3 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-100 rounded" />
        <div className="w-8 h-3 bg-gray-100 rounded" />
      </div>
    </div>
  </div>
);

interface BoardCardProps {
  task: any; // Immutable.js object
  isJira: boolean;
}

/**
 * Board card component.
 * Name displayed prominently at top, attributes below with clean spacing.
 * Time in stage shown as a separate indicator at bottom-right.
 */
const BoardCard = ({ task, isJira }: BoardCardProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const taskType = task.get("type") || "SALE";
  const projectId = task.get("projectId") || "";
  const taskId = task.get("id") || "";

  // Read configurable visible fields from Redux
  const boardCardFields =
    useSelector((state: any) => state.taskReducer.get("boardCardFields")) || fromJS([]);
  const showCardLabels = useSelector((state: any) => state.taskReducer.get("showCardLabels"));

  const visibleFields = useMemo(() => {
    const fields =
      boardCardFields.size > 0 ? boardCardFields.toJS() : DEFAULT_FIELDS_BY_TYPE[taskType];
    return fields.filter((field) => (isJira ? true : field !== "issue"));
  }, [boardCardFields, taskType, isJira]);

  // Extract name separately for prominent display
  const name = task.getIn(["data", "name", "value"]) || task.get("title") || "";

  // Activity counts from Redux store (updated by refresh API → dispatch)
  const totalNotes = getIntField(task, "numberOfNotes");
  const totalActivities = getIntField(task, "numberOfTasks");
  const totalComments = getIntField(task, "numberOfComments");
  const totalEmails = getIntField(task, "numberOfEmails");

  // Lazy refresh: only call API when card is visible AND stats are missing in Redux
  const cardRef = useRef<HTMLDivElement>(null);
  const hasStats = task.getIn(["data", "numberOfComments"]) != null;
  useEffect(() => {
    if (hasStats || !cardRef.current || !taskId) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          refreshInteractionStatistics(taskId)
            .then((item: any) => {
              if (item) {
                // Dispatch to Redux store so all components reflect updated data
                dispatch(TaskAction.updateTaskData(taskId, item, "BOARD"));
              }
            })
            .catch(() => {});
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [taskId, hasStats, dispatch]);

  // Created date for footer (relative days like "27d")
  const createdDateStr =
    task.getIn(["data", "openedAt", "value"]) ||
    task.getIn(["data", "start", "value"]) ||
    task.get("createdAt") ||
    "";
  const { label: createdDateFormatted } = useMemo(
    () => formatRelativeDays(createdDateStr),
    [createdDateStr]
  );

  // Days since last activity (time in stage indicator)
  const lastActivityDateStr = task.getIn(["data", "lastActivity", "value"]) || "";
  const { label: daysAgo, days: daysAgoNum } = useMemo(
    () => formatRelativeDays(lastActivityDateStr),
    [lastActivityDateStr]
  );

  // Navigation handler - optionally opens a specific tab in the detail view
  const handleCardClick = useCallback(
    (tab?: string) => {
      const tabParam = tab ? `&tab=${tab}` : "";
      const appUrl = `/workspaces/${task.get("projectId")}/items/${task.get(
        "id"
      )}?type=${task.get("type")}&view=BOARD${tabParam}`;

      const jiraUrl = getJirItemUrl({
        workspaceId: task.get("projectId"),
        itemId: task.get("id"),
        type: task.get("type"),
        view: "BOARD",
      });

      navigateTo({
        jiraUrlParams: { relativeUrl: jiraUrl },
        appUrl,
        navigate,
      });
    },
    [task, navigate]
  );

  // Time tracking alert colors: >14 days = red, >7 days = orange
  const timeAlertClass =
    daysAgoNum > 14 ? "text-red-500" : daysAgoNum > 7 ? "text-orange-400" : "text-gray-400";

  // Avatar logo from API
  const iconUrl = task.get("iconUrl") || "";

  // Name row icon: show logo if available, otherwise fallback to type icon
  const nameIcon = iconUrl ? (
    <img
      src={iconUrl}
      alt=""
      className="w-3.5 h-3.5 rounded-full object-cover"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = "none";
      }}
    />
  ) : taskType === "CONTACT" ? (
    <User className="w-3.5 h-3.5" />
  ) : (
    <Building2 className="w-3.5 h-3.5" />
  );

  /** Render a single attribute row */
  const renderField = (fieldId: string) => {
    const config = FIELD_CONFIG[fieldId];
    if (!config) return null;

    const value = config.extract(task, taskType);
    const isEmpty = !value && value !== 0;

    if (fieldId === "labels") {
      const rawLabelIds = value ? (value.toJS ? value.toJS() : value) : [];
      const hasLabels = Array.isArray(rawLabelIds) && rawLabelIds.length > 0;
      const labelIds = hasLabels
        ? Array.from(
            new Set(
              rawLabelIds
                .flat(Infinity)
                .map((id: any) =>
                  String(id)
                    .replace(/[\[\]\s]/g, "")
                    .trim()
                )
                .filter((id: string) => id.length > 0 && !id.includes(","))
            )
          )
        : [];
      if (showCardLabels) {
        return (
          <div key={fieldId} className="min-h-[20px]" onClick={(e) => e.stopPropagation()}>
            <div className="text-[12px] font-medium text-[#242529] leading-[16px] mb-1">Labels</div>
            <div className="min-w-0">
              {hasLabels && labelIds.length > 0 ? (
                <LabelsSelect
                  projectId={projectId}
                  contentType={taskType}
                  type="GRID"
                  value={labelIds}
                  onChange={(newLabels: string[]) => {
                    dispatch(TaskAction.updateTask(taskId, "labels", newLabels, taskType, "BOARD"));
                  }}
                />
              ) : (
                <span className="text-xs text-gray-400">Set a value...</span>
              )}
            </div>
          </div>
        );
      }
      return (
        <div
          key={fieldId}
          className="flex items-center gap-1.5 min-h-[20px]"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="shrink-0 w-4 h-4 flex items-center justify-center text-gray-400">
            <Tag className="w-3.5 h-3.5" />
          </span>
          <div className="flex-1 min-w-0">
            {hasLabels && labelIds.length > 0 ? (
              <LabelsSelect
                projectId={projectId}
                contentType={taskType}
                type="GRID"
                value={labelIds}
                onChange={(newLabels: string[]) => {
                  dispatch(TaskAction.updateTask(taskId, "labels", newLabels, taskType, "BOARD"));
                }}
              />
            ) : (
              <span className="text-xs text-gray-400">Set a value...</span>
            )}
          </div>
        </div>
      );
    }

    const icon = config.icon;

    if (isEmpty) {
      return (
        <AttrRow key={fieldId} icon={icon} label={config.label} showLabel={showCardLabels}>
          <span className="text-gray-400">Set a value...</span>
        </AttrRow>
      );
    }

    const rendered = config.render
      ? config.render(value, taskType)
      : typeof value === "string"
        ? value
        : String(value);

    return (
      <AttrRow key={fieldId} icon={icon} label={config.label} showLabel={showCardLabels}>
        {rendered}
      </AttrRow>
    );
  };

  // Hover card with delayed open/close
  const hoverCard = useHoverCard();

  if (taskType === "SALE") {
    const labels = task.getIn(["data", "labels", "value"]) || fromJS([]);
    const dealValue = task.getIn(["data", "estimatedRevenue", "value"]);
    const probability = task.getIn(["data", "estimatedProbability", "value"]);
    const hasProbability = parseFloat(probability) > 0 ? true : false;

    /** Render a single SALE field by its ID, keeping the existing UI structure */
    const renderSaleField = (fieldId: string) => {
      switch (fieldId) {
        case "name":
          return null; // Name is rendered separately above
        case "labels":
          return labels.size > 0 ? (
            <LabelPicker
              key={fieldId}
              colorMode={true}
              type={task.get("type")}
              projectId={projectId}
              labels={labels}
            />
          ) : null;
        case "estimatedRevenue":
          return (
            <div key={fieldId} className={"w-full h-6 space-x-2 flex truncate"}>
              <div>
                <AmountField amount={dealValue} />
              </div>
              {/* Probability is always shown alongside revenue */}
              {visibleFields.includes("estimatedProbability") && (
                <div className={"text-gray-500"}>{hasProbability && `${probability}%`}</div>
              )}
              <div className={"absolute right-1.5"}>
                <ActivityIndicator deal={task} />
              </div>
            </div>
          );
        case "estimatedProbability":
          // Rendered inline with estimatedRevenue above; if revenue is not visible, render standalone
          if (visibleFields.includes("estimatedRevenue")) return null;
          return hasProbability ? (
            <div key={fieldId} className={"w-full h-6 space-x-2 flex truncate"}>
              <div className={"text-gray-500"}>{`${probability}%`}</div>
              <div className={"absolute right-1.5"}>
                <ActivityIndicator deal={task} />
              </div>
            </div>
          ) : null;
        case "issue":
          return isJira ? (
            <div key={fieldId} className={"w-full"}>
              {showCardLabels && (
                <div className="text-[12px] font-medium text-[#242529] leading-[16px] mb-1">
                  Issue
                </div>
              )}
              <div className="min-w-0">
                <LinkItemPickerV2
                  showTriggerHeight={true}
                  projectId={projectId}
                  type={"TASK"}
                  items={task.getIn(["data", "issue", "value"])}
                  width="100%"
                  noBorder={true}
                  showIcon={!showCardLabels}
                  onChange={(issue) => {
                    dispatch(TaskAction.updateTask(taskId, "issue", issue, taskType, "BOARD"));
                  }}
                />
              </div>
            </div>
          ) : null;
        case "contact":
          const primaryContactId =
            task.getIn(["data", "contact", "value", "id"]) ||
            task.getIn(["data", "contact", "value", "value"]);
          return (
            <div key={fieldId} className={"w-full"}>
              {showCardLabels && (
                <div className="text-[12px] font-medium text-[#242529] leading-[16px] mb-1">
                  Contact
                </div>
              )}
              <div className="min-w-0">
                {primaryContactId ? (
                  <HoverManager
                    itemId={primaryContactId}
                    itemType="CONTACT"
                    label={task.getIn(["data", "contact", "value", "label"])}
                    projectId={task.getIn(["data", "contact", "value", "projectId"])}
                  >
                    <LinkItemPickerV2
                      showTriggerHeight={true}
                      projectId={projectId}
                      type={"CONTACT"}
                      items={task.getIn(["data", "contact", "value"])}
                      noBorder={true}
                      showInfoIcon={true}
                      showIcon={!showCardLabels}
                      width="100%"
                      onChange={(contact) => {
                        dispatch(
                          TaskAction.updateTask(taskId, "contact", contact, taskType, "BOARD")
                        );
                      }}
                    />
                  </HoverManager>
                ) : (
                  <LinkItemPickerV2
                    showTriggerHeight={true}
                    projectId={projectId}
                    type={"CONTACT"}
                    items={task.getIn(["data", "contact", "value"])}
                    noBorder={true}
                    showInfoIcon={true}
                    showIcon={!showCardLabels}
                    width="100%"
                    onChange={(contact) => {
                      dispatch(
                        TaskAction.updateTask(taskId, "contact", contact, taskType, "BOARD")
                      );
                    }}
                  />
                )}
              </div>
            </div>
          );
        case "account":
          const primaryAccountId =
            task.getIn(["data", "account", "value", "id"]) ||
            task.getIn(["data", "account", "value", "value"]);
          return (
            <div key={fieldId} className="w-full">
              {showCardLabels && (
                <div className="text-[12px] font-medium text-[#242529] leading-[16px] mb-1">
                  Account
                </div>
              )}
              <div className="min-w-0">
                {primaryAccountId ? (
                  <HoverManager
                    itemId={primaryAccountId}
                    itemType="COMPANY"
                    label={task.getIn(["data", "account", "value", "label"])}
                    projectId={task.getIn(["data", "account", "value", "projectId"])}
                  >
                    <LinkItemPickerV2
                      showTriggerHeight={true}
                      projectId={projectId}
                      type={"COMPANY"}
                      items={task.getIn(["data", "account", "value"])}
                      noBorder={true}
                      showInfoIcon={false}
                      showIcon={!showCardLabels}
                      width="100%"
                      onChange={(account) => {
                        dispatch(
                          TaskAction.updateTask(taskId, "account", account, taskType, "BOARD")
                        );
                      }}
                    />
                  </HoverManager>
                ) : (
                  <LinkItemPickerV2
                    showTriggerHeight={true}
                    projectId={projectId}
                    type={"COMPANY"}
                    items={task.getIn(["data", "account", "value"])}
                    noBorder={true}
                    showInfoIcon={false}
                    showIcon={!showCardLabels}
                    width="100%"
                    onChange={(account) => {
                      dispatch(
                        TaskAction.updateTask(taskId, "account", account, taskType, "BOARD")
                      );
                    }}
                  />
                )}
              </div>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div className="w-full space-y-1">
        {visibleFields.map((fieldId: string) => renderSaleField(fieldId))}
      </div>
    );
  }

  return (
    <div ref={cardRef} onClick={() => handleCardClick()} className="w-full cursor-pointer">
      {/* Name row with icon and hover info dialog */}
      {name && (
        <>
          <div
            ref={hoverCard.anchorRef}
            className="flex items-center gap-1.5 mb-1.5 pr-5"
            onMouseEnter={hoverCard.open}
            onMouseLeave={hoverCard.scheduleClose}
          >
            <span className="shrink-0 w-4 h-4 flex items-center justify-center text-gray-400">
              {nameIcon}
            </span>
            <span className="text-[13px] font-semibold text-gray-900 leading-5 truncate hover:underline">
              {name}
            </span>
          </div>
          {/* Hover info dialog - rendered via Portal to avoid overflow/z-index issues */}
          {hoverCard.visible &&
            createPortal(
              <div
                className="fixed"
                style={{
                  top: hoverCard.position.top,
                  left: hoverCard.position.left,
                  transform: "translateY(-100%)",
                  zIndex: 9999,
                }}
                onMouseEnter={hoverCard.keepOpen}
                onMouseLeave={hoverCard.scheduleClose}
              >
                <NameHoverCard
                  task={task}
                  taskType={taskType}
                  onClickName={(tab) => handleCardClick(tab)}
                />
              </div>,
              document.body
            )}
        </>
      )}

      {/* Attribute Rows */}
      <div className="space-y-1">
        {visibleFields
          .filter((fieldId: string) => fieldId !== "name")
          .map((fieldId: string) => renderField(fieldId))}
      </div>

      {/* Footer - Activity Stats (always show all icons matching Figma) */}
      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <StatItem
            icon={<NotepadText className="w-4 h-4" />}
            count={totalNotes}
            tooltip="Notes"
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick("commented");
            }}
          />
          <StatItem
            icon={<CheckCircle2 className="w-4 h-4" />}
            count={totalActivities}
            tooltip="Tasks"
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick("task");
            }}
          />
          <StatItem
            icon={<MessagesSquare className="w-4 h-4" />}
            count={totalComments}
            tooltip="Comments"
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick("commented");
            }}
          />
          <StatItem
            icon={<Mails className="w-4 h-4" />}
            count={totalEmails}
            tooltip="Emails"
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick("sendEmail");
            }}
          />
        </div>
        {/* Right side: created date + time in stage */}
        <div className="flex items-center gap-3">
          {createdDateFormatted && (
            <TooltipBadge
              icon={<Clock className="w-4 h-4" />}
              text={createdDateFormatted}
              tooltip="Created date"
              className="text-gray-400 hover:text-gray-600"
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
              }}
            />
          )}
          {daysAgo && (
            <TooltipBadge
              icon={<Clock4 className="w-4 h-4" />}
              text={daysAgo}
              tooltip="Time in stage"
              className={`text-xs font-medium hover:opacity-70 transition-opacity ${timeAlertClass}`}
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardCard;
